<template>
  <v-card outlined class="rounded-lg fill-height">
    <v-card-text class="pa-4">

      <!-- Header -->
      <div class="d-flex align-center mb-1">
        <v-avatar size="40" color="primary" class="mr-3" rounded>
          <v-icon color="white">mdi-bullseye-arrow</v-icon>
        </v-avatar>
        <div class="flex-grow-1">
          <div class="subtitle-1 font-weight-bold">Today's Focus</div>
          <div class="caption grey--text">
            {{ focusTasks.length }} task{{ focusTasks.length !== 1 ? 's' : '' }} due today
          </div>
        </div>
        <v-btn text small class="text-capitalize primary--text pa-0" style="min-width:0;" @click="$router.push('/dashboard')">
          Manage
          <v-icon small class="ml-1">mdi-arrow-right</v-icon>
        </v-btn>
      </div>

      <v-divider class="mt-3 mb-3" />

      <!-- Column headers -->
      <div class="d-flex align-center mb-2 px-2">
        <span class="caption font-weight-bold grey--text text--darken-1 flex-grow-1" style="letter-spacing:0.08em;">TASK</span>
        <span class="caption font-weight-bold grey--text text--darken-1" style="width:110px;text-align:right;letter-spacing:0.08em;">STATUS</span>
      </div>

      <!-- Empty state -->
      <div v-if="focusTasks.length === 0" class="text-center py-6">
        <v-icon size="36" color="grey lighten-2">mdi-check-circle-outline</v-icon>
        <div class="caption grey--text mt-2">No pending tasks due today</div>
      </div>

      <!-- Task rows (scrollable list, no pagination) -->
      <div style="max-height:520px;overflow-y:auto;">
        <div
          v-for="task in focusTasks"
          :key="task.id"
          class="d-flex align-center mb-2 pa-3 rounded-lg"
          style="background:#f5f5f5;border:1px solid #e0e0e0;"
        >
          <!-- Task name + pts badge -->
          <div class="flex-grow-1 pr-2 overflow-hidden">
            <div class="body-2" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ task.name }}</div>
            <div class="d-flex align-center mt-1" style="gap:4px;">
              <v-chip x-small color="amber lighten-4" class="amber--text text--darken-3 font-weight-medium" style="height:18px;">+{{ taskGrowthPts(task) }} pts</v-chip>
            </div>
          </div>

          <!-- Status dropdown -->
          <v-menu offset-y left :disabled="updatingId === task.id">
            <template #activator="{ on }">
              <v-chip
                small
                :color="statusChipColor(task)"
                :text-color="statusChipText(task)"
                class="flex-shrink-0 font-weight-medium"
                style="cursor:pointer;min-width:90px;justify-content:center;"
                v-on="on"
              >
                <v-progress-circular v-if="updatingId === task.id" indeterminate size="10" width="2" class="mr-1" />
                {{ task.status ? task.status.name : 'To Do' }}
                <v-icon x-small class="ml-1" v-if="updatingId !== task.id">mdi-chevron-down</v-icon>
              </v-chip>
            </template>
            <v-list dense>
              <v-list-item
                v-for="s in availableStatuses"
                :key="s.id"
                :disabled="task.status_id === s.id"
                @click="changeStatus(task, s)"
              >
                <v-list-item-title class="body-2">{{ s.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'TodaysFocusCard',
  inject: ['personalTasks', 'statuses', 'optimisticUpdateTask'],
  data() {
    return {
      updatingId: null,
    };
  },
  computed: {
    todayStr() {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    // Statuses excluding recurring (7); we don't show recurring tasks in this card.
    availableStatuses() {
      return (this.statuses() || []).filter(s => s.id !== 7);
    },
    focusTasks() {
      // Only show To Do (1) and In Progress (2) tasks due today.
      const today = this.todayStr;
      const all = [...(this.personalTasks() || [])];
      const seen = new Set();
      return all.filter(t => {
        if (t.deleted_at || seen.has(t.id)) return false;
        seen.add(t.id);

        // Only To Do (1) and In Progress (2)
        if (t.status_id !== 1 && t.status_id !== 2) return false;

        // Must have a due date that is today
        if (!t.due_date) return false;
        return String(t.due_date).slice(0, 10) === today;
      }).sort((a, b) => {
        // In-progress before to-do for visual momentum.
        const inProg = t => t.status_id === 2 ? 0 : 1;
        return inProg(a) - inProg(b);
      });
    },
    overdueCount() {
      return 0;
    },
  },
  mounted() {},
  watch: {
  },
  methods: {

    taskGrowthPts(task) {
      // Spec: Urgent/High priority = 2pts, others = 1pt
      return [1, 2].includes(task.priority_id) ? 2 : 1;
    },
    isOverdue(task) {
      return task.due_date && String(task.due_date).slice(0, 10) < this.todayStr;
    },
    statusChipColor(task) {
      const name = (task.status?.name || '').toLowerCase();
      if (name.includes('progress')) return 'amber darken-1';
      if (name.includes('block')) return 'red lighten-4';
      return 'grey lighten-2';
    },
    statusChipText(task) {
      const name = (task.status?.name || '').toLowerCase();
      if (name.includes('progress')) return 'white';
      if (name.includes('block')) return 'red darken-2';
      return 'grey darken-1';
    },
    async changeStatus(task, status) {
      if (this.updatingId === task.id) return;
      this.updatingId = task.id;
      try {
        await this.optimisticUpdateTask(
          task.id,
          { status_id: status.id, status: status },
          async () => {
            const res = await this.$axios.post(`api/tasks/update_task/${task.id}`, { status_id: status.id });
            if (!res.data.response) throw new Error(res.data.message);
            return res.data;
          }
        );
        this.$toast && this.$toast.success(`Status updated to "${status.name}"`);
      } catch (e) {
        this.$toast && this.$toast.error('Failed to update status');
      } finally {
        this.updatingId = null;
      }
    },
  },
};
</script>
